import { ExternalLinkIcon } from "@chakra-ui/icons";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  Flex,
  FormLabel,
  Icon,
  Link,
  Text,
} from "@chakra-ui/react";
import React from "react";
import { RiArchiveLine, RiDeleteBin6Line, RiEditLine } from "react-icons/ri";
import { AdminCard } from "src/components/Layout/AdminCard";
import { WithRequiredHasuraRoles } from "src/components/Permissions/WithRequiredHasuraRoles";
import { useEditFormTemplateContext } from "src/scenes/orgAdmin/formTemplates/ContentTab/V2/context";
import { useFormTemplateContext } from "src/scenes/orgAdmin/formTemplates/formTemplateContext";
import * as AF from "src/types/formTemplate";
import { HasuraRole } from "src/types/hasuraRole";
import * as RD from "src/types/remoteData";
import { QuestionChangesIndicator } from "../../ChangesIndicator";
import { ImportError } from "../../ImportError";
import { ValidationAlert } from "../../ValidationAlert";
import {
  hasArchivedTag,
  QuestionArchivedTag,
} from "../QuestionExtraInfo/ArchivedTag";
import {
  hasInternalOnlyTag,
  InternalOnlyTag,
} from "../QuestionExtraInfo/InternalOnlyTag";
import {
  hasRequirementTag,
  RequirementTag,
} from "../QuestionExtraInfo/RequirementTag";
import {
  hasVerificationTags,
  VerificationTag,
} from "../QuestionExtraInfo/VerificationTag";
import { SchoolItem } from "../types";
import { AvailableAnswers } from "./AvailableAnswers";
import { MoveQuestionPopover } from "./MoveQuestionPopover";
import { QuestionTitle } from "./QuestionTitle";
import { QuestionType } from "./QuestionType";

type ViewProps = {
  question: AF.V2.Question;
  onEditButtonClick?: () => void;
  onRemoveButtonClick?: () => void;
  onArchiveButtonClick?: () => void;
  onUnarchiveButtonClick?: () => void;
  schools: RD.RemoteData<unknown, SchoolItem[]>;
};
export const QuestionFormView: React.FC<ViewProps> = ({
  question,
  onEditButtonClick,
  onRemoveButtonClick,
  onArchiveButtonClick,
  onUnarchiveButtonClick,
  schools,
}) => {
  const { mode } = useFormTemplateContext();
  const context = useEditFormTemplateContext();
  const { invalidQuestionVisibility } = context;
  const hasInvalidQuestionVisibilty = invalidQuestionVisibility.includes(
    question.id
  );

  const showRemoveButton = context.helpers.isUnpublishedQuestion(question.id);
  const actionsDisabled = !!question.isArchived;

  return (
    <AdminCard
      alert={
        <Flex direction="column" gap="2" width="100%">
          {hasInvalidQuestionVisibilty ? (
            <Alert rounded="md" status="warning">
              <AlertIcon />
              <AlertDescription>
                This question may no longer be conditionally revealed because
                its originating question was removed or changed, check its
                configurations
              </AlertDescription>
            </Alert>
          ) : null}
          <ValidationAlert id={question.id} />
          <ImportError id={question.id} />
        </Flex>
      }
      tag={
        <>
          <Tags question={question} />
          <QuestionChangesIndicator id={question.id} />
        </>
      }
      title={<QuestionTitle question={question} />}
      actions={
        <WithRequiredHasuraRoles
          roles={[HasuraRole.ADMIN, HasuraRole.ORG_ADMIN]}
        >
          {/* Show Archive/Unarchive button for existing questions, Remove button for new questions */}
          {showRemoveButton
            ? onRemoveButtonClick &&
              mode === "edit" && (
                <Button
                  leftIcon={<RiDeleteBin6Line />}
                  colorScheme="gray"
                  variant="outline"
                  onClick={onRemoveButtonClick}
                >
                  Remove question
                </Button>
              )
            : question.isArchived
            ? onUnarchiveButtonClick &&
              mode === "edit" && (
                <Button
                  leftIcon={<RiArchiveLine />}
                  colorScheme="gray"
                  variant="outline"
                  onClick={onUnarchiveButtonClick}
                >
                  Unarchive question
                </Button>
              )
            : onArchiveButtonClick &&
              mode === "edit" && (
                <Button
                  leftIcon={<RiArchiveLine />}
                  colorScheme="gray"
                  variant="outline"
                  onClick={onArchiveButtonClick}
                  isDisabled={actionsDisabled}
                >
                  Archive question
                </Button>
              )}
          {mode === "edit" ? (
            <MoveQuestionPopover
              questionId={question.id}
              isDisabled={actionsDisabled}
            />
          ) : null}
          {onEditButtonClick && mode === "edit" && (
            <Button
              colorScheme="gray"
              variant="outline"
              leftIcon={<RiEditLine />}
              onClick={onEditButtonClick}
              isDisabled={actionsDisabled}
            >
              Edit question
            </Button>
          )}
        </WithRequiredHasuraRoles>
      }
    >
      <Flex direction="column" gap="3">
        <Flex direction="column" gap="4">
          <QuestionType questionType={question.type} />
          <AvailableAnswers question={question} schools={schools} />
        </Flex>
        <Flex direction="column" gap="1">
          <QuestionLink question={question} />
        </Flex>
      </Flex>
    </AdminCard>
  );
};

type QuestionLinkProps = { question: AF.V2.Question };
const QuestionLink: React.FC<QuestionLinkProps> = ({ question }) =>
  question.link_text && question.link_url ? (
    <>
      <FormLabel as={Text} margin="0">
        Question link
      </FormLabel>
      <Link
        href={question.link_url}
        target="_blank"
        color="primary.500"
        alignItems="center"
        display="flex"
      >
        {question.link_text}
        <Icon marginLeft="5px" as={ExternalLinkIcon} />
      </Link>
      <Text fontSize="sm" color="gray.500">
        {question.link_url}
      </Text>
    </>
  ) : null;

type TagsProps = { question: AF.V2.Question };
const Tags: React.FC<TagsProps> = ({ question }) => {
  if (!hasTags(question)) {
    return null;
  }

  return (
    <Flex direction="row" gap="3">
      <VerificationTag question={question} />
      <RequirementTag question={question} />
      <InternalOnlyTag question={question} />
      <QuestionArchivedTag question={question} />
    </Flex>
  );
};

function hasTags(question: AF.V2.Question): boolean {
  return (
    hasVerificationTags(question) ||
    hasRequirementTag(question) ||
    hasInternalOnlyTag(question) ||
    hasArchivedTag(question)
  );
}
